/**
 * Responsibility: Implement temporal behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState, Clock } from "@anyquest/shared-core"
// ============================================================
// TEMPORAL.TS — Clock & Entropy Logic
// 1-minute increments. 24h = Entropy Shift (trait reshuffle).
// ============================================================

export interface TemporalAdvanceResult {
  entropyShiftTriggered: boolean
  newClock: Clock
}

/** Convert a Clock to total minutes elapsed since day 1 00:00. */
export function totalMinFromClock(clock: Clock): number {
  return clock.day * 24 * 60 + clock.hour * 60 + clock.minute
}

export function advanceClock(state: GameState, minutes: number): TemporalAdvanceResult {
  const clock = state.world.clock
  let totalMinutes = totalMinFromClock(clock) + minutes
  const day = Math.floor(totalMinutes / (24 * 60))
  totalMinutes -= day * 24 * 60
  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes % 60
  const previousDay = clock.day
  const entropyShiftTriggered = day > previousDay
  const newClock: Clock = { day, hour, minute }
  // Mutate state directly (triggers Proxy listener)
  state.world.clock.day = newClock.day
  state.world.clock.hour = newClock.hour
  state.world.clock.minute = newClock.minute
  return { entropyShiftTriggered, newClock }
}

export function formatClock(clock: Clock): string {
  return `${String(clock.hour).padStart(2, '0')}:${String(clock.minute).padStart(2, '0')}`
}

export function formatDay(clock: Clock): string {
  return `Day ${clock.day}`
}
